/**
 * Fingerprint utility
 *
 * Computes a deterministic ~7-char base36 hash (FNV-1a 32-bit) over
 * `source + message + topFrame.file + topFrame.line` to group recurring
 * errors. Messages are normalized first so id-only differences collapse.
 *
 * Note: fingerprints are not stable across builds when minification changes
 * file paths or line numbers — acceptable for our grouping use case.
 */

export interface FingerprintParts {
  source?: string;
  message: string;
  topFrame?: { file: string; line: number };
}

// ISO 8601 datetime with optional fractional seconds and timezone (Z or ±HH:MM/±HHMM).
// Stripped first so its embedded digits aren't consumed piecemeal by LONG_DIGIT_RUN_RE.
const ISO_DATETIME_RE = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?/g;
const UUID_RE = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g;
const MONGO_HEX_RE = /\b[0-9a-fA-F]{24}\b/g;
const LONG_DIGIT_RUN_RE = /\d{5,}/g;

function normalizeMessage(message: string): string {
  return message
    .replace(ISO_DATETIME_RE, '')
    .replace(UUID_RE, '')
    .replace(MONGO_HEX_RE, '')
    .replace(LONG_DIGIT_RUN_RE, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function fnv1a32(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

export function computeFingerprint(parts: FingerprintParts): string {
  const message = normalizeMessage(parts.message);
  const file = (parts.topFrame?.file ?? '').toLowerCase();
  const line = parts.topFrame?.line ?? 0;
  const source = parts.source ?? '';
  const input = `${source}|${message}|${file}:${line}`;
  return fnv1a32(input).toString(36);
}
